import { useRef, useState } from 'react';
import { Button, Collapse, Divider, Form, notification } from 'antd';
import { ArrowRightOutlined, CheckOutlined, SyncOutlined, DeleteFilled } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { useParams } from 'react-router-dom';

// services
import * as ActivityService from '../../../../../services/bb-api/activity';
import { MomentGenerator } from '../../../../shared/moment-generator';
import { DateSelectorList } from '../../../../shared/date-selector-list';
import styled from 'styled-components';

import { txt } from '../../../../../locales/i18n';

export function DatesTab({ activity }: any): JSX.Element {
	const [updateLoading, setUpdateLoading] = useState<boolean>(false);

	const formRef = useRef<any>(null);
	const params: any = useParams<any>();
	const activityId: number = params.id;

	async function handleUpdate(values: any): Promise<void> {
		if (values.dates.length === 0) {
			notification.error({ message: 'Minimum one date required' });
			return;
		}

		setUpdateLoading(true);

		try {
			const dateSets: [Moment, Moment][] = values.dates.filter((dateSet: any) => {
				return Array.isArray(dateSet);
			});

			await ActivityService.updateDates(activityId, dateSets);
			notification.success({ message: 'Update Successful' });
		} catch (error) {
			notification.error({
				message: (error as Error).name,
				description: (error as Error).message,
			});
		}

		setUpdateLoading(false);
	}

	function createMomentSets() {
		if (activity.activeDates.includes(null)) {
			return [];
		}

		const momentSets: [Moment, Moment] = activity.activeDates.map((record: any) => {
			const start: Moment = moment(record.start_date);
			const end: Moment = moment(record.end_date);
			return [start, end];
		});

		return momentSets;
	}

	function setMomentSets(momentSets: [Moment, Moment][]): void {
		formRef.current.setFields([{ name: 'dates', value: momentSets }]);
	}

	return (
		<StyledDiv>
			<MomentGenerator setMomentSets={setMomentSets} />
			<Divider />

			<Collapse defaultActiveKey={activity.dates.length === 0 ? ['1'] : []}>
				<Collapse.Panel header={txt('dates.passed dates')} key="1">
					{activity.previousDates.map((date: any, index: number) => {
						const dateFormat: string = 'ddd DD/MM-YY HH:mm';
						const startDateString: string = moment(date.start_date).format(dateFormat);
						const endDateString: string = moment(date.start_date).format(dateFormat);

						return (
							<div key={index} className="date-container">
								{startDateString}&nbsp;&nbsp;&nbsp;
								<ArrowRightOutlined />
								&nbsp;&nbsp;&nbsp;{endDateString}
							</div>
						);
					})}
				</Collapse.Panel>
			</Collapse>

			<Form
				ref={formRef}
				onFinish={handleUpdate}
				initialValues={{ dates: createMomentSets() }}
				className="dates-container"
			>
				<DateSelectorList name="dates" />

				<Form.Item>
					<Button type="primary" htmlType="submit" loading={updateLoading}>
						{txt('global.update')} <CheckOutlined />
					</Button>

					<Button type="ghost" onClick={() => formRef?.current?.resetFields()}>
						{txt('global.reset')} <SyncOutlined />
					</Button>

					<Button
						type="ghost"
						danger
						onClick={() => formRef.current.setFields([{ name: 'dates', value: [] }])}
					>
						{txt('global.clear')} <DeleteFilled />
					</Button>
				</Form.Item>
			</Form>
		</StyledDiv>
	);
}

const StyledDiv = styled.div`
	.ant-collapse {
		max-width: 26rem;
		margin-bottom: 2em;

		.date-container {
			font-size: 1em;
			margin: 0.6em 0;
		}
	}
`;
